/**
 * Phase 328 wave 4 — the BOUNDED conditional-visibility evaluator.
 *
 * This module is the renderer half of ARD-DSL-037. The declaration half lives in
 * `packages/workflow-schema/src/schema/form-grammar.ts` (FORM_PREDICATE_OPS,
 * FORM_PREDICATE_MAX_DEPTH, FORM_PREDICATE_PATH_ROOTS) and is enforced at deploy time; this
 * file evaluates exactly that grammar and nothing wider.
 *
 * ── The anti-DSL line ─────────────────────────────────────────────────────────────────────
 * A predicate is a STRUCTURE, never an expression. There is no string grammar to parse, so
 * there is nothing to evaluate: no `eval`, no `new Function`, no template execution anywhere
 * in this app — asserted by a grep fence in `src/fences.test.ts`. Widening the operator set
 * means widening FORM_PREDICATE_OPS *and* this file in lock-step, never ad hoc.
 *
 * ── Resolution ────────────────────────────────────────────────────────────────────────────
 *   `ctx.<dotted.path>`      → direct key lookup on the validate response's `context`
 *                              snapshot, whose keys are the VERBATIM predicate paths
 *                              (FormsApi.BuildContextSnapshot). No traversal here, so a dot
 *                              inside a segment can never be misread.
 *   `fields.<declared_name>` → the subject's current answer for that field. Exactly one
 *                              segment; group subfields are group-scoped and unaddressable.
 *   `row.<sibling>`          → Phase 515: THIS ROW's own sibling subfield, inside a repeating
 *                              group. Exactly one segment. A row is the ONLY thing that can
 *                              answer it, so a scope carrying no row resolves it to unset.
 *   `item.<dotted.path>`     → (Phase 523) a DOTTED read into the one envelope row a ROW FILTER
 *                              is deciding about, through the same reader the mapping half uses
 *                              (`paths.ts`). Unlike `fields.`, a row is a JSON value rather than a
 *                              flat answer map, so `item.a.b` is a traversal.
 * An unresolvable path yields `undefined` — "unset", which is exactly how the four ops
 * already treat a missing value.
 *
 * ── One grammar, two positions ────────────────────────────────────────────────────────────
 * A ROW FILTER is this same evaluator, run with a row in scope instead of a form. What differs is
 * only what a leaf may ADDRESS, and that difference is declared, not conventional: `visible_when`
 * is validated against FORM_PREDICATE_PATH_ROOTS (`fields`/`ctx`) and a row filter against
 * FORM_ROW_FILTER_PATH_ROOTS (`item`), which is why `item` is not a third predicate root. Here, a
 * path simply resolves against whatever its scope carries — and a `visible_when` scope carries no
 * row, so an `item.` path there is `undefined` and every existing evaluation is unchanged.
 *
 * ── Comparison ────────────────────────────────────────────────────────────────────────────
 * Every answer is compared through its SCALAR PROJECTION SET: a scalar projects to one
 * string (`true` → `"true"`, `7` → `"7"`), a multi-select projects to one string per
 * selection, and an object (a group row) projects to none. For a scalar answer this is
 * exactly the grammar's string comparison; for a multi-select, `eq` reads as "this option is
 * selected" and `in` as "any selected option is in the set". That keeps all four ops TOTAL
 * over the value domain the grammar defines, with no operator inventing a new meaning.
 *
 * ── Failure posture ───────────────────────────────────────────────────────────────────────
 * FAIL-OPEN: a malformed or over-deep node evaluates to VISIBLE. Deploy-time validation is
 * the real gate (a bad predicate never reaches a subject), so the only thing this posture
 * decides is what a subject sees if one ever did — and showing a field the subject can answer
 * is strictly safer than silently hiding one that the workflow needs.
 */
import type {
  FormValues,
  PredicateLeaf,
  PredicateOp,
  PredicateScalar,
  VisibleWhen,
} from "../types.js";
import { ITEM_PREFIX, readPath } from "./paths.js";

/** The CLOSED operator whitelist — mirrors FORM_PREDICATE_OPS. */
export const PREDICATE_OPS: readonly PredicateOp[] = ["eq", "ne", "in", "truthy"];

/**
 * Maximum nesting depth of a predicate tree, root at depth 1 — mirrors
 * FORM_PREDICATE_MAX_DEPTH. A node deeper than this is not evaluated (fail-open), which is
 * what makes evaluation bounded by construction rather than by convention.
 */
export const PREDICATE_MAX_DEPTH = 3;

/**
 * The roots a leaf `path` may address — see "One grammar, two positions" above.
 *
 * Phase 549 — `ctx.` is EXPORTED for the same reason `fields.` below is: `default_from` addresses
 * it too now (ARD-DSL-036's third root), and a root spelled twice in this app is how the two drift
 * apart. Note what makes it unlike the other two: a ctx reference is a DOTTED path and the served
 * snapshot is keyed by the whole thing, prefix included, so a consumer keeps the VERBATIM string
 * rather than slicing a bare one-segment key off it ({@link resolvePath}'s ctx arm).
 */
export const CTX_PREFIX = "ctx.";
/**
 * The `fields.` root — exported because `default_from` (Phase 449) addresses the SAME
 * vocabulary a `visible_when` leaf does (FORM_PREDICATE_PATH_ROOTS' `fields`), and one root
 * spelled twice in this app is how the two drift apart.
 */
export const FIELDS_PREFIX = "fields.";
/**
 * Phase 515 — the `row.` root (FORM_PREDICATE_ROW_PATH_TEMPLATE): a group SUBFIELD gating on
 * an EARLIER-declared sibling subfield OF ITS OWN ROW. Deploy-time validation (ARD-DSL-037)
 * already guarantees it appears nowhere else, so this file only has to answer it correctly
 * where a row IS in scope, and to answer "unset" where one is not.
 */
export const ROW_PREFIX = "row.";

/**
 * Phase 515 — the `party.` root (FORM_DEFAULT_FROM_PARTY_TEMPLATE): a `default_from` source
 * naming a key of the PARTY ROW this form's token is registered against. It is a
 * `default_from`-only root — no `visible_when` leaf addresses it — but it is spelled HERE, once,
 * beside the roots it shares a namespace with, because a root spelled twice in this app is how
 * the two drift apart.
 */
export const PARTY_PREFIX = "party.";

/**
 * Phase 532 — the ONE other spelling of a ctx reference the BROWSER has to resolve: the
 * `${ctx.<dotted.path>}` template a DISPLAY ROW names its value by (a step card's `rows`, a
 * `from_ctx.evidence` entry — ARD-DSL-051/052).
 *
 * A predicate leaf carries a BARE dotted path (`ctx.risk_score.band`); a display row carries the
 * ADR-009 path-only TEMPLATE (`${ctx.risk_score.band}`). Two spellings of one thing, and the
 * server already normalises them to a single snapshot key in one place
 * (`FormsApi.CollectDisplayRowCtxPaths`, on `PathOnlyGrammar`). This is the client half of that
 * same normalisation, mirrored character-for-character — including the segment alphabet, so a
 * template this app accepts is exactly one the deploy-time grammar does.
 */
const CTX_ROW_TEMPLATE = /^\$\{ctx((?:\.[a-zA-Z_][a-zA-Z0-9_]*)+)\}$/;

/**
 * The snapshot key a display row's `${ctx.…}` path resolves by, or `null` when the string is not
 * a path-only ctx template (a literal, a mixed expression, anything else a declaration might
 * hold). Never throws: an unreadable path is "no value", exactly as it is for a predicate leaf.
 */
export function ctxRowKey(path: unknown): string | null {
  if (typeof path !== "string") return null;
  const match = CTX_ROW_TEMPLATE.exec(path);
  return match === null ? null : CTX_PREFIX + match[1]!.slice(1);
}

/**
 * The value behind a display row's `${ctx.…}` path, read out of the SERVED snapshot by the same
 * verbatim-key lookup {@link resolvePath} uses. `undefined` when the declaration named a path the
 * snapshot does not carry — which the caller renders as an explicit "not recorded", never a blank.
 */
export function readCtxRow(path: unknown, ctx: Readonly<Record<string, unknown>>): unknown {
  const key = ctxRowKey(path);
  if (key === null) return undefined;
  return Object.prototype.hasOwnProperty.call(ctx, key) ? ctx[key] : undefined;
}

/**
 * What a predicate or a default is evaluated against: the server's ctx snapshot (keyed by
 * verbatim path), the subject's current answers, the whitelisted party snapshot, and — inside a
 * repeating group only — THE ROW being walked.
 */
export interface PredicateScope {
  readonly ctx: Readonly<Record<string, unknown>>;
  readonly fields: Readonly<FormValues>;
  /**
   * Phase 515 — the validate response's `party` snapshot: ONLY the `party_ref` keys this
   * schema's own `default_from: party.<key>` pointers name (FormsApi `BuildPartySnapshot`),
   * keyed BARE (one segment, no prefix — its own namespace, unlike `ctx`'s verbatim paths).
   *
   * A SEPARATE member from `fields` on purpose: a party key and a top-level field name can
   * collide (`name`, `email` — the very keys a KYC party row carries), so folding one into the
   * other would let a carry-forward silently resolve against the wrong namespace. It rides the
   * SAME scope object every predicate and every default already receives, so a caller cannot
   * forget to pass it where it is needed.
   *
   * Empty `{}` for a job-level token, a token with no ledger row, or a schema that declares no
   * `party.` pointer — the three cases the server distinguishes as `null` / `null` / `{}` and
   * which are indistinguishable HERE precisely because all three mean "no party default".
   */
  readonly party: Readonly<Record<string, unknown>>;
  /**
   * Phase 515 — the repeating-group row a subfield's `visible_when` is being evaluated
   * against. OPTIONAL, because the form-level scope has no row: every walk that is NOT
   * per-row leaves it absent, and a `row.` path then resolves to `undefined` = unset, the
   * same fail-safe posture `ctx.` and `fields.` already have for a path they cannot answer.
   *
   * Only {@link rowScope} sets it. A per-row walk that builds the scope by hand is the bug
   * this member exists to make impossible, which is why there is one constructor for it.
   */
  readonly row?: Readonly<FormValues>;
  /**
   * Phase 523 — the ONE upstream row being filtered, present only where the evaluator runs as a
   * ROW FILTER. OPTIONAL, and absent at every `visible_when` site: a field's visibility is decided
   * with no row in scope, so an `item.` path there resolves to "unset" exactly as an unknown root
   * always has.
   */
  readonly item?: unknown;
}

/**
 * THE row-augmented scope — the one constructor of a `row.`-answering scope in this app.
 *
 * Every per-row subfield walk (validation, payload projection, the summary line, the row's
 * completeness check, the row's rendered controls, the review card) takes its scope from
 * here, so "a subfield is judged against ITS OWN row" is one rule in one place rather than
 * six near-copies of a spread. A walk that passes the FORM scope instead is not a visible
 * bug — it silently evaluates every row against the same wrong values — so the source fence
 * in `fences.test.ts` pins this helper as the only caller of `visibleFields(<group>.fields)`.
 */
export function rowScope(scope: PredicateScope, row: Readonly<FormValues>): PredicateScope {
  return { ...scope, row };
}

/**
 * Is a field / step visible? An absent `visible_when` is unconditionally visible — that is
 * what makes the whole extension additive over the flat single-page contract.
 */
export function isVisible(visibleWhen: VisibleWhen | undefined | null, scope: PredicateScope): boolean {
  if (visibleWhen === undefined || visibleWhen === null) return true;
  return evaluate(visibleWhen, scope, 1);
}

/**
 * Evaluate one predicate node at `depth` (root = 1). Exported for direct unit coverage of the
 * grammar; the renderer calls {@link isVisible}.
 */
export function evaluate(node: unknown, scope: PredicateScope, depth = 1): boolean {
  if (depth > PREDICATE_MAX_DEPTH) return true; // bounded by construction — see failure posture
  if (typeof node !== "object" || node === null || Array.isArray(node)) return true;

  const candidate = node as Partial<PredicateLeaf> & { all?: unknown; any?: unknown };

  if (Array.isArray(candidate.all)) {
    return (candidate.all as VisibleWhen[]).every((child) => evaluate(child, scope, depth + 1));
  }
  if (Array.isArray(candidate.any)) {
    return (candidate.any as VisibleWhen[]).some((child) => evaluate(child, scope, depth + 1));
  }
  if (typeof candidate.path !== "string" || typeof candidate.op !== "string") return true;
  if (!PREDICATE_OPS.includes(candidate.op as PredicateOp)) return true;

  return evaluateLeaf(candidate as PredicateLeaf, scope);
}

function evaluateLeaf(leaf: PredicateLeaf, scope: PredicateScope): boolean {
  const resolved = resolvePath(leaf.path, scope);

  switch (leaf.op) {
    case "truthy":
      return isTruthy(resolved);
    case "eq":
      return matchesScalar(resolved, leaf.value);
    case "ne":
      return !matchesScalar(resolved, leaf.value);
    case "in": {
      if (!Array.isArray(leaf.value)) return false;
      const projected = projections(resolved);
      return leaf.value.some((member) => projected.includes(normalise(member)));
    }
    /* c8 ignore next 2 — unreachable: op is whitelisted before the switch. */
    default:
      return true;
  }
}

/**
 * Resolves a leaf `path` to the subject's answer, this row's cell, the ctx snapshot value, or
 * the filtered row.
 */
export function resolvePath(path: string, scope: PredicateScope): unknown {
  if (path.startsWith(CTX_PREFIX)) {
    // Verbatim-key lookup: the snapshot is keyed by the whole path, prefix included.
    return Object.prototype.hasOwnProperty.call(scope.ctx, path) ? scope.ctx[path] : undefined;
  }
  if (path.startsWith(FIELDS_PREFIX)) {
    const name = path.slice(FIELDS_PREFIX.length);
    if (name.length === 0 || name.includes(".")) return undefined; // exactly one segment
    return Object.prototype.hasOwnProperty.call(scope.fields, name) ? scope.fields[name] : undefined;
  }
  if (path.startsWith(ROW_PREFIX)) {
    const name = path.slice(ROW_PREFIX.length);
    if (name.length === 0 || name.includes(".")) return undefined; // exactly one segment
    // No row in scope ⇒ unset. It is NOT a fall-back to `scope.fields`: a row cell and a
    // top-level answer of the same name are different values, and silently reading one for
    // the other is precisely the namespace collapse the roots exist to prevent.
    const row = scope.row;
    if (row === undefined) return undefined;
    return Object.prototype.hasOwnProperty.call(row, name) ? row[name] : undefined;
  }
  if (path.startsWith(ITEM_PREFIX)) {
    // A DOTTED read into the row, through the SAME own-property / unsafe-segment reader the
    // mapping half uses. With no row in scope the root is absent, and `readPath` already answers
    // an absent root with `undefined` — so this branch needs no site-specific guard to leave a
    // `visible_when` evaluation exactly as it was.
    return readPath(scope.item, path.slice(ITEM_PREFIX.length));
  }
  return undefined;
}

/** `eq` in one place, so `ne` can be its exact negation. */
function matchesScalar(resolved: unknown, declared: PredicateLeaf["value"]): boolean {
  if (declared === undefined || declared === null || Array.isArray(declared)) return false;
  return projections(resolved).includes(normalise(declared));
}

/**
 * The scalar projection set of an answer. A group row (object) projects to nothing — a
 * repeating group is not a comparable value, and the grammar never asks it to be.
 */
function projections(value: unknown): string[] {
  if (value === undefined || value === null) return [];
  if (Array.isArray(value)) return value.flatMap((item) => projections(item));
  if (typeof value === "string") return [value];
  if (typeof value === "number" || typeof value === "boolean") return [String(value)];
  return [];
}

/** The declared comparison value as its comparison string (`true` → `"true"`, `7` → `"7"`). */
function normalise(value: PredicateScalar): string {
  return String(value);
}

/**
 * `truthy` — present and not empty / false / "". Exported because it is the app's ONE
 * definition of "this value counts as set": the lookup mapping fills a `boolean` field
 * through the same rule the grammar compares with, rather than inventing a second one.
 */
export function isTruthy(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 && trimmed.toLowerCase() !== "false";
  }
  if (Array.isArray(value)) return value.length > 0;
  return Object.keys(value as object).length > 0;
}
